import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { Trash2 } from 'lucide-react'
import { api, ApiError } from '../../api/client'
import { Button } from '../../components/ui/Button'
import { ShareButton } from '../../components/ui/ShareButton'
import { PageHeader } from '../../components/ui/PageHeader'
import { LoadingDots } from '../../components/ui/LoadingDots'
import { ErrorState } from '../../components/ui/ErrorState'
import { SectionedMarkdown } from '../../components/ui/SectionedMarkdown'
import { useSession } from '../../store/user.store'
import { formatBirthDate } from '../../lib/date-format'
import { usePaywallStore } from '../../store/paywall.store'

export function CompatDetailRoute() {
  const { id } = useParams<{ id: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const showPaywall = usePaywallStore((s) => s.show)
  const lang = (useSession((s) => s.user?.language) ?? 'ru') as 'ru' | 'en'

  const query = useQuery({
    queryKey: ['compat', id],
    queryFn: () => api.getCompat(id!),
    enabled: Boolean(id),
  })

  const unlock = useMutation({
    mutationFn: () => api.unlockCompatFull(id!),
    onSuccess: (data) => {
      qc.setQueryData(['compat', id], data)
      void qc.invalidateQueries({ queryKey: ['compat', 'list'] })
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 402) {
        showPaywall({
          reason: 'feature_locked',
          feature: 'compat_full',
        })
      }
    },
  })

  const remove = useMutation({
    mutationFn: () => api.deleteCompat(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['compat'] })
      toast.success(t('compat_actions.deleted'))
      navigate('/compat')
    },
  })

  function confirmDelete() {
    if (!query.data) return
    if (window.confirm(t('profile.delete_compat_confirm', { name: query.data.partnerName }))) {
      remove.mutate()
    }
  }

  if (query.isLoading) return <LoadingDots compact />
  if (query.isError || !query.data) return <ErrorState onRetry={() => query.refetch()} />

  const c = query.data

  return (
    <div className="flex flex-col gap-5 pb-6">
      <PageHeader
        title={c.partnerName}
        backTo="/compat"
        right={
          <button
            onClick={confirmDelete}
            disabled={remove.isPending}
            className="flex items-center gap-1 rounded-full bg-copper/10 px-3 py-1.5 text-sm text-copper transition hover:bg-copper/20 disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.8} aria-hidden />
            <span>{t('compat_actions.delete')}</span>
          </button>
        }
      />

      <div className="flex flex-col items-center gap-2 rounded-3xl bg-white/70 p-6 text-center ring-1 ring-sage-200/60">
        <p className="text-xs text-sage-500">{formatBirthDate(c.partnerBirthDate, lang)}</p>
        <p className="font-serif text-6xl text-copper">{t('compat.score', { score: c.score.score })}</p>
        <p className="text-sage-700">{c.teaserText}</p>
      </div>

      {c.tier === 'full' && c.fullText ? (
        <section>
          <h2 className="mb-3 font-serif text-xl text-sage-800">{t('compat.teaser_label')}</h2>
          <SectionedMarkdown>{c.fullText}</SectionedMarkdown>
        </section>
      ) : (
        <div className="flex flex-col gap-3">
          <Button size="lg" loading={unlock.isPending} onClick={() => unlock.mutate()}>
            {t('compat.open_full')}
          </Button>
          <ShareButton
            size="md"
            label={t('compat.invite_partner')}
            url={c.inviteLink}
            text={t('compat.invite_text')}
          />
        </div>
      )}
    </div>
  )
}
